import type { JiraIssueLink } from '@shared/types.js';
import { getOpenBlockers, describeLinks } from '@/lib/issue-links.js';

interface LinkIndicatorProps {
  links: JiraIssueLink[] | undefined;
}

/**
 * Compact link summary for cards and table rows: a red "blocked" marker while
 * any blocker is unresolved, plus a link count. Hover lists every relation.
 */
export function LinkIndicator({ links }: LinkIndicatorProps) {
  if (!links || links.length === 0) return null;
  const openBlockers = getOpenBlockers(links);
  const title = describeLinks(links);

  return (
    <span className="inline-flex shrink-0 items-center gap-1" title={title}>
      {openBlockers.length > 0 && (
        <span
          className="inline-flex items-center gap-0.5 rounded-[3px] bg-red-50 px-1 py-px text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-200"
          aria-label={`Blocked by ${openBlockers.map((l) => l.issue.key).join(', ')}`}
        >
          <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden="true">
            <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth={1.8} />
            <path d="M3.8 12.2l8.4-8.4" stroke="currentColor" strokeWidth={1.8} />
          </svg>
          {openBlockers.length > 1 ? openBlockers.length : 'Blocked'}
        </span>
      )}
      <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-500" aria-label={`${links.length} linked work items`}>
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
          <path d="M6.5 9.5l3-3M7 4.5l1-1a2.5 2.5 0 013.5 3.5l-1 1M9 11.5l-1 1A2.5 2.5 0 014.5 9l1-1" strokeLinecap="round" />
        </svg>
        {links.length}
      </span>
    </span>
  );
}
